import {
  type FrontendInstaller,
  otherInstalledIntegration,
} from "@p0security/integrations-installer";
import type { SnowflakeAiComponents } from "@p0security/integrations-snowflake-ai-shared/types";
import type { ItemConfigOf } from "@p0security/shared/install/types";
import { Typography } from "@p0security/ui";

import {
  type InstallContext,
  awsStorePrivateKey,
  awsTf,
  gcpStorePrivateKey,
  gcpTf,
  p0ProviderTf,
} from "./iac.ts";
import { keyPairCommand, setupSql, setupTf } from "./snowflake-setup.ts";

type SecretManagerConfig = ItemConfigOf<
  SnowflakeAiComponents["credential"]
>["secretManager"];

type SecretManagerType = SecretManagerConfig["type"];

type InstallCommands = ReturnType<
  NonNullable<
    NonNullable<
      FrontendInstaller<SnowflakeAiComponents>["components"]["credential"]
    >["instructions"]
  >
>;

// TODO(INT-617): placeholder, the docs page published at launch decides
// this link.
const DOCS_URL = "https://docs.p0.dev/integrations/resource-integrations";

const { Link, Paragraph } = Typography;

const NewPreamble: React.FC = () => (
  <>
    <Paragraph>
      P0 gives each AI agent request its own short-lived Snowflake user, with
      only the roles or object privileges the request names, through a connector
      that you deploy in AWS or Google Cloud.
    </Paragraph>
    <Paragraph>
      The connector signs in as the <code>P0_AI_CONNECTOR</code> service user,
      whose role holds USERADMIN and SECURITYADMIN. These let it create and drop
      users and roles, and grant any role or privilege in the account, so treat
      its private key as an administrator credential.{" "}
      <Link href={DOCS_URL} target="_blank">
        Learn more
      </Link>
      .
    </Paragraph>
  </>
);

/** The install steps around one cloud's connector Terraform, its command
 * that stores the private key, and P0's Terraform provider's resources for the
 * install. */
const connectorInstructions = (
  accountId: string,
  connector: {
    cloud: string;
    tf: string;
    storePrivateKey: string;
    providerTf: string;
  }
): InstallCommands => ({
  commands: {
    iac: [
      {
        header: "Generate the connector user's key pair.",
        command: keyPairCommand,
      },
      {
        header:
          "Apply the following Terraform to set up Snowflake and deploy P0's Snowflake connector, passing the public key as p0_connector_public_key.",
        command: `${setupTf(accountId)}\n${connector.tf}`,
      },
      {
        header:
          'Add the private key to its secret, then click "Next". The connector can\'t sign in until the secret holds the key.',
        command: connector.storePrivateKey,
      },
      {
        header:
          "To manage this install with P0's Terraform provider, add these resources too.",
        command: connector.providerTf,
      },
    ],
    // The connector's Terraform alone, so the Snowflake setup is created
    // once, by the SQL.
    // TODO(INT-614): placeholder, Miguel decides whether the shell tab
    // deploys the connector with gcloud (gcloud run deploy and its IAM
    // bindings) rather than Terraform.
    shell: [
      {
        header: "Generate the connector user's key pair.",
        command: keyPairCommand,
      },
      {
        header: `Apply the following Terraform to deploy P0's Snowflake connector in ${connector.cloud}. It doesn't set up Snowflake: the SQL below does.`,
        command: connector.tf,
      },
      {
        header: "Run this SQL in Snowflake as ACCOUNTADMIN:",
        command: setupSql(),
      },
      {
        header:
          'Add the private key to its secret, then click "Next". The connector can\'t sign in until the secret holds the key.',
        command: connector.storePrivateKey,
      },
    ],
  },
});

// Azure Key Vault isn't offered: the connector doesn't run on Azure.
const credentialInstructions: Record<
  SecretManagerType,
  (
    context: InstallContext,
    accountId: string,
    secretManager: SecretManagerConfig
  ) => InstallCommands
> = {
  "aws-sm": (context, accountId, secretManager) =>
    secretManager.type === "aws-sm"
      ? connectorInstructions(accountId, {
          cloud: "AWS",
          tf: awsTf(context, accountId, secretManager),
          storePrivateKey: awsStorePrivateKey(accountId, secretManager),
          providerTf: p0ProviderTf(accountId, secretManager),
        })
      : { commands: {} },
  "azure-kv": () => ({ commands: {} }),
  "gcp-sm": (context, accountId, secretManager) =>
    secretManager.type === "gcp-sm"
      ? connectorInstructions(accountId, {
          cloud: "Google Cloud",
          tf: gcpTf(context, accountId, secretManager),
          storePrivateKey: gcpStorePrivateKey(accountId, secretManager),
          providerTf: p0ProviderTf(accountId, secretManager),
        })
      : { commands: {} },
};

export const snowflakeAiInstaller: FrontendInstaller<SnowflakeAiComponents> = {
  components: {
    credential: {
      newPreamble: () => <NewPreamble />,
      instructions: (context, id, item) =>
        credentialInstructions[item.secretManager.type](
          context,
          id,
          item.secretManager
        ),
      items: {
        secretManager: {
          options: {
            "aws-sm": {
              items: {
                install: {
                  optionProvider: otherInstalledIntegration({
                    integration: "aws-sm",
                    component: "iam-write",
                  }),
                },
              },
            },
            "gcp-sm": {
              items: {
                install: {
                  optionProvider: otherInstalledIntegration({
                    integration: "gcp-sm",
                    component: "iam-write",
                  }),
                },
              },
            },
          },
        },
      },
    },
  },
};
